import {
  EMAIL_SENT_MESSAGE,
  EMAIL_VALIDATION_MESSAGE,
  ERROR_MESSAGE,
  SEND_EMAIL_API,
} from '@/common/constants/constants';
import { Divider } from '@/components/Divider';
import { TicketAdmission } from '@/components/Layout/LeftPane/TicketAdmission';
import { useCSRFToken } from '@/pages/contextProvider';
import { Attendee, Order } from '@/types';
import validateEmail from '@/utils/validateEmail';
import { HEADER_NAME } from '@eventbrite/csrf-double-cookie';
import {
  AlertOutlineIcon,
  LoadingButton,
  SuccessIcon,
  TextField,
  Typography,
  getFormValidationAdornment,
} from '@eventbrite/marmalade';
import moment from 'moment-timezone';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import { PageTitle } from '../PageTitle';
import { TicketFoundEmailTemplate } from './TicketEmailTemplate';
import styles from './TicketFound.module.scss';

export interface TicketFoundComponentProps {
  order: Order;
  attendeesList: Attendee[];
  setCurrentPage: (page: 'ticketDetails') => void;
}

export const TicketFound: React.FunctionComponent<TicketFoundComponentProps> = (
  order,
  attendeesList,
) => {
  const csrfToken = useCSRFToken();
  const formattedOrder = order.order;
  const formattedAttendeesList = order.attendeesList;

  const eventStartTimezone = formattedOrder?.event?.start?.timezone || 'UTC';
  const eventStartLocalTime = formattedOrder?.event?.start?.local || '';
  const ticketFoundTitle = formattedOrder.noOfTickets > 1 ? 'Tickets found' : 'Ticket found';
  const eventFormattedStartTime = moment
    .tz(eventStartLocalTime, eventStartTimezone)
    .format('ddd, MMM D • h:mma z');

  const eventEndTimezone = formattedOrder?.event?.start?.timezone || 'UTC';
  const eventEndLocalTime = formattedOrder?.event?.start?.local || '';

  const eventFormattedEndTime = moment
    .tz(eventEndLocalTime, eventEndTimezone)
    .format('ddd, MMM D • h:mm A z');

  const orderFormattedDate = moment(formattedOrder.creationDate).format('ddd, MMM D, YYYY');

  const formattedCost = () => {
    const displayCost = (
      formattedOrder.totalCost.amount / Math.pow(10, formattedOrder.totalCost.currencyPrecision)
    ).toFixed(formattedOrder.totalCost.currencyPrecision);
    const formatter = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: formattedOrder.totalCost.currencyCode,
    });

    return formatter.format(parseFloat(displayCost)).replace('US', '');
  };

  const isMultipleTickets = formattedOrder.noOfTickets > 1;

  const [error, setError] = useState(false);
  const [helperText, setHelperText] = useState(
    `Where should we send your ticket${isMultipleTickets ? 's' : ''}?`,
  );

  const [email, setEmail] =
    useState<string>(() => sessionStorage.getItem(formattedOrder.orderId) as string) || '';

  const [isLoading, setIsLoading] = useState(false);

  const [isEmailSent, setIsEmailSent] = useState(
    () => sessionStorage.getItem(`emailSent_${formattedOrder.orderId}`) === 'true',
  );

  useEffect(() => {
    sessionStorage.setItem(`emailSent_${formattedOrder.orderId}`, isEmailSent.toString());
    sessionStorage.setItem(formattedOrder.orderId, email);
  }, [isEmailSent, formattedOrder.orderId, email]);

  const handleOnEmailTicketsClick = async () => {
    setIsLoading(true);
    if (!validateEmail(email)) {
      setError(true);
      setHelperText(EMAIL_VALIDATION_MESSAGE);
      setIsLoading(false);
      return;
    }

    const send_email_body = TicketFoundEmailTemplate({
      order: formattedOrder,
      attendeesList: formattedAttendeesList,
      recepientEmail: email,
    });

    try {
      const response = await fetch(SEND_EMAIL_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [HEADER_NAME]: csrfToken,
        },
        body: JSON.stringify(send_email_body),
      });
      if (response.ok) {
        setError(false);
        setIsEmailSent(true);
        sessionStorage.setItem(formattedOrder.orderId, email);
        setHelperText(EMAIL_SENT_MESSAGE);
        setIsLoading(false);
      } else {
        setError(true);
        setHelperText(ERROR_MESSAGE);
        setIsEmailSent(false);
        sessionStorage.setItem(formattedOrder.orderId, email);
        setIsLoading(false);
      }
    } catch (err) {
      setError(true);
      setIsEmailSent(false);
      setHelperText(ERROR_MESSAGE);
      setIsLoading(false);
    }
  };

  return (
    <>
      {' '}
      <PageTitle pageTitle={ticketFoundTitle} />
      <div className={styles.content_block}>
        <Typography variant="body-md">
          {`We found an order. You can email your ticket${isMultipleTickets ? 's' : ''} now.`}
        </Typography>
        <div className={styles.event_details_block}>
          <Typography variant="heading-sm" className={styles.event_title}>
            {formattedOrder.event.name.text}
          </Typography>
          <Typography variant="body-md-bold" className={styles.event_datetime}>
            {eventFormattedStartTime}
          </Typography>
          <Link
            href={formattedOrder.event.url}
            target="_blank"
            className={styles.event_details_link}
          >
            <Typography variant="body-md-bold" className={styles.event_details_link}>
              View event details
            </Typography>
          </Link>
          <Divider />
          <div className={styles.number_cost_block}>
            <Typography variant="heading-sm" className={styles.ticket_number}>
              {formattedOrder.noOfTickets} {isMultipleTickets ? 'x' : ''} ticket
              {isMultipleTickets ? 's' : ''}
            </Typography>
            <Typography variant="heading-sm" className={styles.ticket_cost}>
              {formattedCost()}
            </Typography>
          </div>
          <Typography variant="body-md" className={styles.purchase_date}>
            Purchased on: {orderFormattedDate}
          </Typography>
          <Typography variant="body-md">Order ID: #{formattedOrder.orderId}</Typography>
          {!isEmailSent ? (
            <>
              <div className={styles.email_input}>
                <TextField
                  id="email-tickets"
                  label="Email address"
                  value={email}
                  required
                  type="text"
                  helperText={helperText}
                  data-automation="email-tickets"
                  onChange={(e) => setEmail(e)}
                  crossOrigin={undefined}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                  hasError={!!error}
                  validationAdornment={getFormValidationAdornment({
                    hasError: !!error,
                    isValid: validateEmail(email),
                  })}
                />
              </div>
              <div className={styles.info_container}>
                <AlertOutlineIcon size="small" className={styles.info_icon} />
                <Typography variant="body-md" className={styles.text}>
                  You can only send one email. Please make sure your email address is correct.
                </Typography>
              </div>
              <div className={styles.ticket_found_submit}>
                <LoadingButton
                  isLoading={isLoading}
                  size="block"
                  variant="primary"
                  className={styles.submit_button}
                  onClick={handleOnEmailTicketsClick}
                  data-heap-id="tickets_found_email_tickets"
                >
                  {`Email ticket${isMultipleTickets ? 's' : ''}`}
                </LoadingButton>
              </div>
            </>
          ) : (
            <div className={styles.success_container}>
              <SuccessIcon size="small" className={styles.success_icon} />
              <Typography variant="body-md" className={styles.text}>
                {`Ticket${isMultipleTickets ? 's' : ''} sent to ${email}`}
              </Typography>
            </div>
          )}
        </div>

        <div>
          <Typography variant="heading-sm">{`Your ticket${isMultipleTickets ? 's' : ''}`}</Typography>
          {/* <Button variant="secondary" className={styles.download_ticket_btn}>
          Download ticket
          <DownloadIcon size="small" className={styles.download_icon} />
        </Button> */}
          {formattedAttendeesList.map((attendee, index) => (
            <TicketAdmission
              key={attendee.barcode.id}
              attendee={attendee as Attendee}
              index={index as number}
              noOfTickets={formattedOrder.noOfTickets as number}
            />
          ))}
        </div>
      </div>
    </>
  );
};
